import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';

const Login = ({ supabase }) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [showSignUp, setShowSignUp] = useState(false);
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [message, setMessage] = useState('');
  const [showCodeEntry, setShowCodeEntry] = useState(false);
  const [otpCode, setOtpCode] = useState('');

  const handleSendOtp = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: !showSignUp ? false : true,
          emailRedirectTo: window.location.origin,
          data: showSignUp ? { username } : undefined,
        },
      });

      if (error) throw error;
      
      setMessage(`Check your email for the verification code!`);
      setShowCodeEntry(true);
      
    } catch (error) {
      setError(error.message || 'Error sending verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: 'email',
      });

      if (error) throw error;
      
      // Successful verification is handled by the auth state change listener
      setSuccess('Verification successful!');
      
    } catch (error) {
      setError(error.message || 'Error verifying code');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setUsername('');
    setError('');
    setSuccess('');
    setMessage('');
    setShowCodeEntry(false);
    setOtpCode('');
  };

  const toggleForm = () => {
    setShowSignUp(!showSignUp);
    resetForm();
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: !showSignUp ? false : true,
          data: showSignUp ? { username } : undefined,
        },
      });

      if (error) throw error;
      
      setMessage('A new verification code has been sent to your email!');
      
    } catch (error) {
      setError(error.message || 'Error sending verification code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-10 flex flex-col items-center justify-center max-w-md">
      <Card className="w-full">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold">Kappa Sig Die</CardTitle>
        </CardHeader>

        <CardContent>
          {error && (
            <div className="mb-6 p-3 rounded-md bg-destructive/10 text-destructive" role="alert">
              {error}
            </div>
          )}
          
          {success && (
            <div className="mb-6 p-3 rounded-md bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" role="alert">
              {success}
            </div>
          )}

          {message && (
            <div className="mb-6 p-3 rounded-md bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" role="alert">
              {message}
            </div>
          )}

          {showCodeEntry ? (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="otp-code" className="text-sm font-medium">Verification Code</label>
                <input
                  type="text"
                  id="otp-code"
                  className="w-full p-2 border rounded-md bg-background"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  required
                  placeholder="Enter the code from your email"
                  disabled={loading}
                  autoComplete="one-time-code"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
              
              <div className="text-center mt-4">
                <button
                  type="button"
                  className="text-sm text-primary hover:underline"
                  onClick={handleResendCode}
                  disabled={loading}
                >
                  Didn't receive a code? Send again
                </button>
              </div>
            </form>
          ) : showSignUp ? (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="username" className="text-sm font-medium">Username</label>
                <input
                  type="text"
                  id="username"
                  className="w-full p-2 border rounded-md bg-background"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  placeholder="Enter your username"
                  disabled={loading}
                />
              </div>
              
              <div className="space-y-1">
                <label htmlFor="signup-email" className="text-sm font-medium">Email</label>
                <input
                  type="email"
                  id="signup-email"
                  className="w-full p-2 border rounded-md bg-background"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                  disabled={loading}
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Sending Code...' : 'Send Verification Code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="login-email" className="text-sm font-medium">Email</label>
                <input
                  type="email"
                  id="login-email"
                  className="w-full p-2 border rounded-md bg-background"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                  disabled={loading}
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Sending Code...' : 'Send Verification Code'}
              </Button>
            </form>
          )}
        </CardContent>
          
        <CardFooter className="flex justify-center border-t pt-6">
          {showCodeEntry ? (
            <button 
              type="button" 
              className="text-primary hover:underline"
              onClick={resetForm}
              disabled={loading}
            >
              Cancel and start over
            </button>
          ) : showSignUp ? (
            <button 
              type="button" 
              className="text-primary hover:underline"
              onClick={toggleForm}
              disabled={loading}
            >
              Already have an account? Log In
            </button>
          ) : (
            <button 
              type="button" 
              className="text-primary hover:underline"
              onClick={toggleForm}
              disabled={loading}
            >
              Need an account? Sign Up
            </button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default Login;